/**
 * Advanced component loading utilities with performance optimizations
 */

import React, { lazy, ComponentType, LazyExoticComponent } from 'react'
import { prefetchRoute } from './performanceOptimizer'

/**
 * Cache for loaded components to avoid re-importing
 */
const componentCache = new Map<string, LazyExoticComponent<any>>()

/**
 * Create a lazy component with enhanced error handling and caching
 */
export function createLazyComponent<T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    componentName?: string
): LazyExoticComponent<T> {
    const cacheKey = componentName || importFn.toString()

    // Return cached component if available
    if (componentCache.has(cacheKey)) {
        return componentCache.get(cacheKey) as LazyExoticComponent<T>
    }

    const LazyComponent = lazy(() =>
        importFn().catch((error) => {
            console.error(`Failed to load component ${componentName || 'Unknown'}:`, error)

            // Return a fallback component
            return {
                default: (() => React.createElement('div', {
                    className: 'p-4 text-center text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg'
                }, [
                    React.createElement('p', { key: 'error' }, 'Failed to load component'),
                    React.createElement('button', {
                        key: 'retry',
                        onClick: () => window.location.reload(),
                        className: 'mt-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700'
                    }, 'Retry')
                ])) as unknown as T,
            }
        })
    )

        // Add display name for debugging
        ; (LazyComponent as any).displayName = `Lazy(${componentName || 'Component'})`

    // Cache the component
    componentCache.set(cacheKey, LazyComponent)

    return LazyComponent
}

/**
 * Preload a component for better performance
 */
export function preloadComponent<T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    componentName?: string
): Promise<{ default: T }> {
    const cacheKey = componentName || importFn.toString()

    // Check if already cached
    if (componentCache.has(cacheKey)) {
        return Promise.resolve({ default: {} as T })
    }

    return importFn().then((module) => {
        // Cache the loaded module
        const LazyComponent = lazy(() => Promise.resolve(module))
            ; (LazyComponent as any).displayName = `Lazy(${componentName || 'Component'})`
        componentCache.set(cacheKey, LazyComponent)

        return module
    })
}

/**
 * Component definitions for lazy loading
 */
export const LazyComponents = {
    // Project components
    ProjectGrid: createLazyComponent(
        () => import('@/components/projects/ProjectGrid'),
        'ProjectGrid'
    ),
    ProjectCard: createLazyComponent(
        () => import('@/components/projects/ProjectCard'),
        'ProjectCard'
    ),
    ProjectGallery: createLazyComponent(
        () => import('@/components/projects/ProjectGallery'),
        'ProjectGallery'
    ),
    BlockchainDetails: createLazyComponent(
        () => import('@/components/projects/BlockchainDetails'),
        'BlockchainDetails'
    ),

    // Experience components
    ExperienceTimeline: createLazyComponent(
        () => import('@/components/experience/ExperienceTimeline'),
        'ExperienceTimeline'
    ),
    SkillsVisualization: createLazyComponent(
        () => import('@/components/experience/SkillsVisualization'),
        'SkillsVisualization'
    ),
    EducationSection: createLazyComponent(
        () => import('@/components/experience/EducationSection'),
        'EducationSection'
    ),

    // UI components
    CodeBlock: createLazyComponent(
        () => import('@/components/ui/CodeBlock'),
        'CodeBlock'
    ),
    MathEquation: createLazyComponent(
        () => import('@/components/ui/MathEquation'),
        'MathEquation'
    ),
    RichTextTable: createLazyComponent(
        () => import('@/components/ui/RichTextTable'),
        'RichTextTable'
    ),

    // Performance monitoring (development only)
    PerformanceMonitor: createLazyComponent(
        () => import('@/components/performance/PerformanceMonitor'),
        'PerformanceMonitor'
    ),
}

/**
 * Preload critical components based on route
 */
export function preloadCriticalComponents(route: string): void {
    switch (route) {
        case '/':
            // Preload components likely to be used on home page
            preloadComponent(() => import('@/components/projects/ProjectGrid'))
            preloadComponent(() => import('@/components/experience/SkillsVisualization'))
            break

        case '/projects':
            // Preload project-related components
            preloadComponent(() => import('@/components/projects/ProjectGrid'))
            preloadComponent(() => import('@/components/projects/ProjectCard'))
            preloadComponent(() => import('@/components/projects/ProjectFilters'))
            break

        case '/experience':
            // Preload experience-related components
            preloadComponent(() => import('@/components/experience/ExperienceTimeline'))
            preloadComponent(() => import('@/components/experience/SkillsVisualization'))
            preloadComponent(() => import('@/components/experience/EducationSection'))
            break

        case '/blog':
            // Preload blog-related components
            preloadComponent(() => import('@/components/ui/CodeBlock'))
            preloadComponent(() => import('@/components/ui/PortableText'))
            break

        default:
            // Preload common components
            preloadComponent(() => import('@/components/ui/OptimizedImage'))
            break
    }
}

/**
 * Route-based component preloading with intersection observer
 */
export function setupRoutePreloading(): void {
    if (typeof window === 'undefined') return

    // Preload components when hovering over navigation links
    const navLinks = document.querySelectorAll('a[href^="/"]')

    navLinks.forEach((link) => {
        const href = (link as HTMLAnchorElement).getAttribute('href')
        if (!href) return

        let preloadTimeout: NodeJS.Timeout

        link.addEventListener('mouseenter', () => {
            preloadTimeout = setTimeout(() => {
                preloadCriticalComponents(href)
                prefetchRoute(href)
            }, 100) // Small delay to avoid unnecessary preloading
        })

        link.addEventListener('mouseleave', () => {
            if (preloadTimeout) {
                clearTimeout(preloadTimeout)
            }
        })
    })
}

/**
 * Initialize component loading optimizations
 */
export function initializeComponentLoading(): void {
    if (typeof window === 'undefined') return

    // Setup route preloading
    setupRoutePreloading()

    // Preload critical components for current route
    const currentPath = window.location.pathname
    preloadCriticalComponents(currentPath)

    // Clear component cache on page unload to prevent memory leaks
    window.addEventListener('beforeunload', () => {
        componentCache.clear()
    })
}

// Initialize on load
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeComponentLoading)
    } else {
        initializeComponentLoading()
    }
}